
import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import { Link } from "react-router-dom";

import API_BASE_URL from "../config/api";


function Questions() {

    /*
    |--------------------------------------------------------------------------
    | API
    |--------------------------------------------------------------------------
    */

    const API_URL =
        `${API_BASE_URL}/admin/questions`;


    /*
    |--------------------------------------------------------------------------
    | State
    |--------------------------------------------------------------------------
    */

    const [questions, setQuestions] = useState([]);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState("");

    const [search, setSearch] = useState("");

    const [currentPage, setCurrentPage] = useState(1);


    /*
    |--------------------------------------------------------------------------
    | Pagination
    |--------------------------------------------------------------------------
    */

    const itemsPerPage = 10;


    /*
    |--------------------------------------------------------------------------
    | Remove HTML From Question / Explanation
    |--------------------------------------------------------------------------
    */

    const getText = (html) => {

        if (!html) {
            return "";
        }


        const tempDiv =
            document.createElement("div");


        tempDiv.innerHTML = html;


        const text =
            tempDiv.textContent ||
            tempDiv.innerText ||
            "";


        return text.trim();

    };


    /*
    |--------------------------------------------------------------------------
    | Fetch Questions
    |--------------------------------------------------------------------------
    */

    const fetchQuestions = async () => {

        try {

            setLoading(true);

            setError("");


            const response =
                await fetch(
                    API_URL,
                    {
                        method: "GET",

                        headers: {
                            Accept:
                                "application/json"
                        },

                        credentials: "include"
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | Unauthorized
            |--------------------------------------------------------------------------
            */

            if (response.status === 401) {

                throw new Error(
                    "Admin authentication failed. Please login again."
                );

            }


            /*
            |--------------------------------------------------------------------------
            | Server Error
            |--------------------------------------------------------------------------
            */

            if (!response.ok) {

                throw new Error(
                    `Server Error: ${response.status}`
                );

            }


            /*
            |--------------------------------------------------------------------------
            | JSON
            |--------------------------------------------------------------------------
            */

            const data =
                await response.json();


            console.log(
                "Questions API Response:",
                data
            );


            /*
            |--------------------------------------------------------------------------
            | Success
            |--------------------------------------------------------------------------
            */

            if (data.success) {

                setQuestions(
                    Array.isArray(data.questions)
                        ? data.questions
                        : []
                );

            } else {

                setError(
                    data.message ||
                    "Unable to fetch questions."
                );

            }

        } catch (error) {

            console.error(
                "Questions API Error:",
                error
            );


            setError(
                error.message ||
                "Unable to connect with server."
            );

        } finally {

            setLoading(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Fetch On Load
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        fetchQuestions();

    }, []);


    /*
    |--------------------------------------------------------------------------
    | Search
    |--------------------------------------------------------------------------
    */

    const filteredQuestions =
        useMemo(() => {

            const value =
                search
                    .trim()
                    .toLowerCase();


            /*
            |--------------------------------------------------------------------------
            | No Search
            |--------------------------------------------------------------------------
            */

            if (!value) {

                return questions;

            }


            /*
            |--------------------------------------------------------------------------
            | Filter Questions
            |--------------------------------------------------------------------------
            */

            return questions.filter(
                (question) => {


                    const qNumber =
                        String(
                            question.Q_number || ""
                        ).toLowerCase();


                    const questionId =
                        String(
                            question.question_id || ""
                        ).toLowerCase();


                    const questionText =
                        getText(
                            question.question
                        ).toLowerCase();


                    const explanation =
                        getText(
                            question.explanation
                        ).toLowerCase();


                    const paperType =
                        String(
                            question.paper_type || ""
                        ).toLowerCase();


                    const paper =
                        String(
                            question.paper || ""
                        ).toLowerCase();


                    const category =
                        String(
                            question.category || ""
                        ).toLowerCase();


                    const commonId =
                        String(
                            question.common_id || ""
                        ).toLowerCase();


                    return (

                        qNumber.includes(value) ||

                        questionId.includes(value) ||

                        questionText.includes(value) ||

                        explanation.includes(value) ||

                        paperType.includes(value) ||

                        paper.includes(value) ||

                        category.includes(value) ||

                        commonId.includes(value)

                    );

                }
            );

        }, [questions, search]);


    /*
    |--------------------------------------------------------------------------
    | Total Pages
    |--------------------------------------------------------------------------
    */

    const totalPages =
        Math.ceil(
            filteredQuestions.length /
            itemsPerPage
        );


    /*
    |--------------------------------------------------------------------------
    | Start Index
    |--------------------------------------------------------------------------
    */

    const startIndex =
        (currentPage - 1) *
        itemsPerPage;


    /*
    |--------------------------------------------------------------------------
    | Paginated Questions
    |--------------------------------------------------------------------------
    */

    const paginatedQuestions =
        filteredQuestions.slice(
            startIndex,
            startIndex + itemsPerPage
        );


    /*
    |--------------------------------------------------------------------------
    | Search Handler
    |--------------------------------------------------------------------------
    */

    const handleSearch = (event) => {

        setSearch(
            event.target.value
        );

        setCurrentPage(1);

    };


    /*
    |--------------------------------------------------------------------------
    | Previous
    |--------------------------------------------------------------------------
    */

    const handlePrevious = () => {

        if (currentPage > 1) {

            setCurrentPage(
                currentPage - 1
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Next
    |--------------------------------------------------------------------------
    */

    const handleNext = () => {

        if (
            currentPage < totalPages
        ) {

            setCurrentPage(
                currentPage + 1
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (

            <div
                className="
                    container-fluid
                    d-flex
                    justify-content-center
                    align-items-center
                "
                style={{
                    minHeight: "400px"
                }}
            >

                <div className="text-center">

                    <div
                        className="
                            spinner-border
                            text-primary
                        "
                        role="status"
                    >
                    </div>


                    <div
                        className="
                            mt-3
                            text-muted
                        "
                    >
                        Loading questions...
                    </div>

                </div>

            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | Main
    |--------------------------------------------------------------------------
    */

    return (

        <div className="container-fluid">


            {/* =====================================================
                Page Header
            ===================================================== */}

            <div
                className="
                    d-flex
                    justify-content-between
                    align-items-center
                    mb-4
                "
            >

                <div>

                    <h3 className="mb-1">
                        Questions
                    </h3>


                    <p className="text-muted mb-0">
                        Manage questions and paper details
                    </p>

                </div>


                {/* =================================================
                    Top Buttons
                ================================================= */}

                <div className="d-flex gap-2">


                    {/* Refresh */}

                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={fetchQuestions}
                    >

                        <i
                            className="
                                fa
                                fa-refresh
                                me-2
                            "
                        ></i>

                        Refresh

                    </button>


                    {/* Add Question */}

                    <Link
                        to="/add-question"
                        className="btn btn-primary"
                    >

                        <i
                            className="
                                fa
                                fa-plus
                                me-2
                            "
                        ></i>

                        Add Question

                    </Link>

                </div>

            </div>


            {/* =====================================================
                Error
            ===================================================== */}

            {error && (

                <div
                    className="
                        alert
                        alert-danger
                        d-flex
                        justify-content-between
                        align-items-center
                    "
                >

                    <span>
                        {error}
                    </span>


                    <button
                        type="button"
                        className="
                            btn
                            btn-sm
                            btn-danger
                        "
                        onClick={fetchQuestions}
                    >

                        Try Again

                    </button>

                </div>

            )}


            {/* =====================================================
                Card
            ===================================================== */}

            <div className="card shadow-sm">


                {/* =================================================
                    Card Header
                ================================================= */}

                <div className="card-header bg-white">

                    <div className="row align-items-center">


                        {/* Search */}

                        <div className="col-md-6">

                            <div
                                className="input-group"
                            >

                                <span
                                    className="
                                        input-group-text
                                        bg-white
                                    "
                                >

                                    <i
                                        className="
                                            fa
                                            fa-search
                                        "
                                    ></i>

                                </span>


                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="
                                        Search question, Q number, paper or category...
                                    "
                                    value={search}
                                    onChange={
                                        handleSearch
                                    }
                                />

                            </div>

                        </div>


                        {/* Total */}

                        <div
                            className="
                                col-md-6
                                text-md-end
                                mt-3
                                mt-md-0
                            "
                        >

                            <span className="text-muted">
                                Total Questions:
                            </span>


                            <strong className="ms-2">
                                {filteredQuestions.length}
                            </strong>

                        </div>

                    </div>

                </div>


                {/* =================================================
                    Table
                ================================================= */}

                <div className="card-body p-0">

                    <div
                        className="table-responsive"
                        style={{
                            maxHeight: "320px"
                        }}
                    >

                        <table
                            className="
                                table
                                table-bordered
                                table-hover
                                align-middle
                                mb-0
                            "
                        >


                            {/* =================================================
                                Header
                            ================================================= */}

                            <thead
                                className="table-light"
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5
                                }}
                            >

                                <tr>

                                    {/* # */}

                                    <th
                                        className="text-center"
                                        style={{
                                            minWidth: "60px"
                                        }}
                                    >
                                        #
                                    </th>


                                    {/* Q Number */}

                                    <th
                                        className="text-center"
                                        style={{
                                            minWidth: "100px"
                                        }}
                                    >
                                        Q. Number
                                    </th>


                                    {/* Question */}

                                    <th
                                        style={{
                                            minWidth: "400px"
                                        }}
                                    >
                                        Question
                                    </th>


                                    {/* Paper Type */}

                                    <th
                                        style={{
                                            minWidth: "110px"
                                        }}
                                    >
                                        Paper Type
                                    </th>


                                    {/* Paper */}

                                    <th
                                        style={{
                                            minWidth: "150px"
                                        }}
                                    >
                                        Paper
                                    </th>


                                    {/* Category */}

                                    <th
                                        style={{
                                            minWidth: "180px"
                                        }}
                                    >
                                        Category
                                    </th>


                                    {/* Exam Date */}

                                    <th
                                        style={{
                                            minWidth: "130px"
                                        }}
                                    >
                                        Exam Date
                                    </th>


                                    {/* Sort Order */}

                                    <th
                                        className="text-center"
                                        style={{
                                            minWidth: "100px"
                                        }}
                                    >
                                        Sort Order
                                    </th>


                                    {/* Action */}

                                    <th
                                        className="text-center"
                                        style={{
                                            minWidth: "110px"
                                        }}
                                    >
                                        Action
                                    </th>

                                </tr>

                            </thead>


                            {/* =================================================
                                Body
                            ================================================= */}

                            <tbody>

                                {paginatedQuestions.length >
                                0 ? (

                                    paginatedQuestions.map(
                                        (
                                            item,
                                            index
                                        ) => {


                                            const questionText =
                                                getText(
                                                    item.question
                                                );


                                            return (

                                                <tr
                                                    key={
                                                        item.question_id ||
                                                        index
                                                    }
                                                >


                                                    {/* =================================================
                                                        #
                                                    ================================================= */}

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        {
                                                            startIndex +
                                                            index +
                                                            1
                                                        }

                                                    </td>


                                                    {/* =================================================
                                                        Q Number
                                                    ================================================= */}

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        <span
                                                            className="
                                                                badge
                                                                bg-light
                                                                text-dark
                                                                border
                                                            "
                                                        >

                                                            {
                                                                item.Q_number ||
                                                                "-"
                                                            }

                                                        </span>

                                                    </td>


                                                    {/* =================================================
                                                        Question
                                                    ================================================= */}

                                                    <td>

                                                        <div
                                                            title={
                                                                questionText
                                                            }
                                                            style={{
                                                                maxWidth:
                                                                    "500px",
                                                                whiteSpace:
                                                                    "nowrap",
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis"
                                                            }}
                                                        >

                                                            {
                                                                questionText ||
                                                                "-"
                                                            }

                                                        </div>

                                                    </td>


                                                    {/* =================================================
                                                        Paper Type
                                                    ================================================= */}

                                                    <td>

                                                        <span
                                                            className="
                                                                badge
                                                                bg-primary
                                                            "
                                                        >

                                                            {
                                                                item.paper_type ||
                                                                "-"
                                                            }

                                                        </span>

                                                    </td>


                                                    {/* =================================================
                                                        Paper
                                                    ================================================= */}

                                                    <td>

                                                        {
                                                            item.paper ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* =================================================
                                                        Category
                                                    ================================================= */}

                                                    <td>

                                                        {
                                                            item.category ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* =================================================
                                                        Exam Date
                                                    ================================================= */}

                                                    <td>

                                                        {
                                                            item.exam_date ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* =================================================
                                                        Sort Order
                                                    ================================================= */}

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        {
                                                            item.sort_order ??
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* =================================================
                                                        Edit
                                                    ================================================= */}

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        <Link
                                                            to={
                                                                `/edit-question/${encodeURIComponent(
                                                                    item.question_id
                                                                )}`
                                                            }
                                                            className="
                                                                btn
                                                                btn-sm
                                                                btn-outline-primary
                                                            "
                                                            title="Edit Question"
                                                        >

                                                            <i
                                                                className="
                                                                    fa
                                                                    fa-edit
                                                                    me-1
                                                                "
                                                            ></i>

                                                            Edit

                                                        </Link>

                                                    </td>

                                                </tr>

                                            );

                                        }
                                    )

                                ) : (

                                    <tr>

                                        <td
                                            colSpan="10"
                                            className="
                                                text-center
                                                py-5
                                                text-muted
                                            "
                                        >

                                            <i
                                                className="
                                                    fa
                                                    fa-question-circle
                                                    fs-3
                                                    mb-3
                                                "
                                            ></i>


                                            <div>
                                                No questions found.
                                            </div>

                                        </td>

                                    </tr>

                                )}

                            </tbody>

                        </table>

                    </div>

                </div>


                {/* =================================================
                    Pagination
                ================================================= */}

                {filteredQuestions.length > 0 && (

                    <div
                        className="
                            card-footer
                            bg-white
                            d-flex
                            justify-content-between
                            align-items-center
                        "
                    >


                        {/* Showing */}

                        <div
                            className="text-muted"
                        >

                            Showing{" "}

                            <strong>
                                {startIndex + 1}
                            </strong>

                            {" "}to{" "}

                            <strong>
                                {Math.min(
                                    startIndex +
                                    itemsPerPage,
                                    filteredQuestions.length
                                )}
                            </strong>

                            {" "}of{" "}

                            <strong>
                                {filteredQuestions.length}
                            </strong>

                        </div>


                        {/* Buttons */}

                        <div
                            className="btn-group"
                        >

                            {/* Previous */}

                            <button
                                type="button"
                                className="
                                    btn
                                    btn-outline-primary
                                "
                                disabled={
                                    currentPage === 1
                                }
                                onClick={
                                    handlePrevious
                                }
                            >

                                Previous

                            </button>


                            {/* Current Page */}

                            <button
                                type="button"
                                className="
                                    btn
                                    btn-primary
                                "
                                disabled
                            >

                                {currentPage}

                                {" / "}

                                {totalPages || 1}

                            </button>


                            {/* Next */}

                            <button
                                type="button"
                                className="
                                    btn
                                    btn-outline-primary
                                "
                                disabled={
                                    currentPage >=
                                    totalPages
                                }
                                onClick={
                                    handleNext
                                }
                            >

                                Next

                            </button>

                        </div>

                    </div>

                )}

            </div>

        </div>

    );

}


export default Questions;

